import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { email } from '@vuelidate/validators';
import { normalizeBrazilPhone } from '../../NewConversation/helpers/composeConversationHelper';

export const MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export const INPUT_TYPES = {
  EMAIL: 'email',
  TEL: 'tel',
  TEXT: 'text',
};

export const validatePhoneNumber = value => {
  if (!value) return true;
  try {
    // Normalize BR (prepend +55 if needed) before validating.
    // Without this, libphonenumber rejects BR numbers without country code
    // (e.g. "61987898887" -> false because it can't infer the country)
    return isValidPhoneNumber(normalizeBrazilPhone(value));
  } catch (error) {
    return false;
  }
};

export const formatPhoneNumber = value => {
  try {
    const phoneNumber = parsePhoneNumber(value);
    return {
      isValid: phoneNumber?.isValid() || false,
      formattedValue: phoneNumber?.formatInternational() || value,
    };
  } catch (error) {
    return { isValid: false, formattedValue: value };
  }
};

export const getValidationRules = type => ({
  newTag: {
    ...(type === INPUT_TYPES.EMAIL ? { email } : {}),
    ...(type === INPUT_TYPES.TEL ? { isValidPhone: validatePhoneNumber } : {}),
  },
});

export const checkTagTypeValidity = (type, value, v$) => {
  if (type === INPUT_TYPES.TEL) {
    // Don't mark phone as invalid visually — allow any format with 8+ digits
    return value.replace(/[^\d]/g, '').length < 8;
  }
  return v$.$invalid;
};

export const validateAndFormatNewTag = (
  trimmedNewTag,
  type,
  isNewTagInValidType
) => {
  let isValid = true;
  let formattedValue = trimmedNewTag;

  if (type === INPUT_TYPES.EMAIL) {
    isValid = !isNewTagInValidType;
  } else if (type === INPUT_TYPES.TEL) {
    const { isValid: phoneValid, formattedValue: phoneFormatted } =
      formatPhoneNumber(trimmedNewTag);
    // Always allow creating contacts — format if valid, keep raw if not
    isValid = trimmedNewTag.length >= 8;
    formattedValue = phoneValid ? phoneFormatted : trimmedNewTag;
  }

  return { isValid, formattedValue };
};

export const createNewTagMenuItem = (
  formattedValue,
  trimmedNewTag,
  type,
  createLabel = '',
  whatsappStatus = null
) => {
  let description = '';
  let icon = 'i-lucide-user-plus';
  if (type === INPUT_TYPES.TEL) {
    if (whatsappStatus === 'registered') {
      description = 'WHATSAPP_VERIFIED';
      icon = 'i-lucide-check-circle';
    } else if (whatsappStatus === 'not_registered') {
      description = 'NO_WHATSAPP_WARNING';
      icon = 'i-lucide-alert-triangle';
    } else if (whatsappStatus === 'checking') {
      description = 'CHECKING_WHATSAPP';
    }
  }

  // When there's a description sub-line, the label is just the number.
  // Otherwise keep the createLabel prefix (e.g. "Create contact: user@email.com").
  let label = formattedValue;
  if (!description && createLabel) {
    label = `${createLabel} ${formattedValue}`;
  }

  return {
    label,
    value: trimmedNewTag,
    ...(type === INPUT_TYPES.EMAIL ? { email: trimmedNewTag } : {}),
    ...(type === INPUT_TYPES.TEL ? { phoneNumber: trimmedNewTag } : {}),
    icon,
    action: 'create',
    whatsappStatus,
    description,
  };
};

export const buildTagMenuItems = ({
  mode,
  tags,
  menuItems,
  newTag,
  isLoading,
  type,
  isNewTagInValidType,
  createLabel = '',
  whatsappStatus = null,
}) => {
  if (mode === MODE.SINGLE && tags.length >= 1) return [];

  const availableMenuItems = menuItems.filter(
    item => !tags.includes(item.label)
  );

  // Show typed value as suggestion only if:
  // 1. There's a value being typed
  // 2. The value isn't already in the tags
  // 3. Validation passes (email/phone) and There are no menu items available
  const trimmedNewTag = newTag?.trim();
  const shouldShowTypedValue =
    trimmedNewTag &&
    !tags.includes(trimmedNewTag) &&
    !isLoading &&
    !availableMenuItems.length;

  if (shouldShowTypedValue) {
    const { isValid, formattedValue } = validateAndFormatNewTag(
      trimmedNewTag,
      type,
      isNewTagInValidType
    );

    if (isValid) {
      return [
        createNewTagMenuItem(
          formattedValue,
          trimmedNewTag,
          type,
          createLabel,
          whatsappStatus
        ),
      ];
    }
  }

  return availableMenuItems;
};

export const canAddTag = (mode, tagsLength) =>
  !(mode === MODE.SINGLE && tagsLength >= 1);

export const findMatchingMenuItem = (menuItems, value) =>
  menuItems.find(item => item.email === value);
